'use client';

import { useState } from 'react';
import type { ProductCardPromotion } from '@/app_components/AffiliateProductCard/types';
import { Icon } from '@/app_components/common/Icon';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

const iconMap = {
  failed: 'CircleX',
  isCopied: 'CircleCheck',
} as const;

const messageDuration = 1000;

export const Coupon = ({ coupon }: { coupon: ProductCardPromotion }) => {
  const [messageState, setMessageState] = useState<'failed' | 'isCopied'>('isCopied');
  const [toolTip, setToolTip] = useState<boolean>(false);

  const onCopy = () => {
    if (toolTip || coupon.label) {
      return;
    }

    navigator.clipboard
      .writeText(coupon.code)
      .then(() => {
        setToolTip(true);
        setMessageState('isCopied');
        setTimeout(() => setToolTip(false), messageDuration);
      })
      .catch(() => {
        setToolTip(true);
        setMessageState('failed');
        setTimeout(() => setToolTip(false), messageDuration);
      });
  };

  let icon: 'Zap' | 'CircleX' | 'CircleCheck' | 'Copy' = 'Copy';
  if (coupon.label) {
    icon = 'Zap';
  } else if (toolTip) {
    icon = iconMap[messageState];
  }

  return (
    <>
      <button
        className={cn(
          'flex gap-sm typography-xs success-text [&_span]:font-semibold [&_*]:font-graphik-compact font-normal whitespace-normal text-left',
          !coupon.label && 'hover:cursor-pointer',
        )}
        data-cy={ContentPlacement.AffiliateProductCardCouponWrapper}
        onClick={onCopy}
        type='button'
      >
        <Icon
          className='success-text'
          icon={icon}
          size='sm'
        />
        {coupon.label ? (
          <p>{coupon.label}</p>
        ) : (
          <p>
            Use code <span className='font-semibold'>{coupon.code}</span> to get {coupon.discount} off your purchase
          </p>
        )}
      </button>
      <div
        className={cn(
          'absolute flex flex-row gap-sm py-xxxs px-xxs success-surface success-text top-[-33px] font-medium text-xs left-1/2 -translate-x-1/2',
          'transition-all duration-200 ease-out',
          toolTip ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1 pointer-events-none',
        )}
      >
        <Icon
          icon={iconMap[messageState]}
          size='sm'
        />
        <p className='font-graphik-compact text-sm font-medium leading-[140%]'>
          {messageState === 'failed' ? 'Failed to Copy!' : 'Copied!'}
        </p>
      </div>
    </>
  );
};
